import React, { useEffect, useRef, useState } from 'react';

import { Dropdown, DropdownOption } from '../../shared-components/Dropdown/Dropdown';
import { Number as NumberInput } from '../../shared-components/Number/Number';
import {
  CUSTOM_LOGOUT_UNIT_OPTIONS,
  DESKTOP_AND_WEB_LOGOUT_CONTROL_OPTIONS,
  PRESET_LOGOUT_VALUES,
} from '../../utils/commonControlOptions';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';

const MIN_CUSTOM_MINUTES = 10;
const MAX_CUSTOM_MINUTES = 4320; // 72 hours

const getValidationError = (value: string, unit: 'minutes' | 'hours'): string | null => {
  const parsed = parseInt(value, 10);
  if (value === '' || isNaN(parsed)) return 'Please enter a value.';
  if (parsed < 1) return 'Value must be at least 1.';

  const minutes = unit === 'hours' ? parsed * 60 : parsed;
  if (minutes < MIN_CUSTOM_MINUTES) return `Minimum is ${MIN_CUSTOM_MINUTES} minutes.`;
  if (minutes > MAX_CUSTOM_MINUTES) return `Maximum is ${MAX_CUSTOM_MINUTES / 60} hours.`;

  return null;
};

const CustomLogoutInput = ({
  currentValue,
  onChange,
  onValidationChange,
}: {
  currentValue: number;
  onChange: (minutes: number) => void;
  onValidationChange: (error: string | null) => void;
}) => {
  const isHours = currentValue >= 60 && currentValue % 60 === 0;
  const [unit, setUnit] = useState<'minutes' | 'hours'>(isHours ? 'hours' : 'minutes');
  const [inputValue, setInputValue] = useState<string>(
    isHours ? String(currentValue / 60) : String(currentValue)
  );
  const skipNextSyncRef = useRef(false);

  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    const newIsHours = currentValue >= 60 && currentValue % 60 === 0;
    setUnit(newIsHours ? 'hours' : 'minutes');
    setInputValue(newIsHours ? String(currentValue / 60) : String(currentValue));
  }, [currentValue]);

  const handleInputChange = (newValue: string, newUnit: 'minutes' | 'hours') => {
    const error = getValidationError(newValue, newUnit);
    onValidationChange(error);

    if (error) return;

    const parsed = parseInt(newValue, 10);
    const minutes = newUnit === 'hours' ? parsed * 60 : parsed;

    skipNextSyncRef.current = true;
    onChange(minutes);
  };

  const minForUnit = unit === 'hours' ? 1 : MIN_CUSTOM_MINUTES;
  const maxForUnit = unit === 'hours' ? 72 : MAX_CUSTOM_MINUTES;

  return (
    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
      <NumberInput
        dataTestId="autoLogout-custom-value"
        value={inputValue}
        min={minForUnit}
        max={maxForUnit}
        onInputChange={(val) => {
          setInputValue(val);
          handleInputChange(val, unit);
        }}
      />
      <Dropdown
        dataTestId="autoLogout-custom-unit"
        defaultValue={unit}
        options={CUSTOM_LOGOUT_UNIT_OPTIONS as DropdownOption[]}
        onChange={(val) => {
          const newUnit = val as 'minutes' | 'hours';
          setUnit(newUnit);
          handleInputChange(inputValue, newUnit);
        }}
      />
    </div>
  );
};

const CustomAutoLogoutControl = () => {
  const dispatch = useAppDispatch();
  const currentValue = useAppSelector((state) => state.orgSettings.settings.autoLogout);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isCustom =
    currentValue !== undefined && !PRESET_LOGOUT_VALUES.includes(Number(currentValue));
  const dropdownValue = isCustom ? 'custom' : currentValue;

  const handleChange = (value: number) => {
    dispatch(actions.updateSettings({ autoLogout: value }));
  };

  const handleValidationChange = (error: string | null) => {
    setValidationError(error);
    if (error) {
      dispatch(actions.setValidationError(error));
    } else {
      dispatch(actions.clearValidationError());
    }
  };

  return (
    <>
      <Dropdown
        dataTestId="autoLogout dropdown"
        defaultValue={dropdownValue as string}
        onChange={(val) => {
          if (val === 'custom') {
            handleChange(30);
          } else {
            handleChange(Number(val));
            handleValidationChange(null);
          }
        }}
        options={DESKTOP_AND_WEB_LOGOUT_CONTROL_OPTIONS as DropdownOption[]}
      />
      {isCustom && (
        <CustomLogoutInput
          currentValue={currentValue as number}
          onChange={handleChange}
          onValidationChange={handleValidationChange}
        />
      )}
      {validationError && (
        <p
          data-test-id="autoLogout-validation-error"
          style={{ color: 'var(--red-500, #d32f2f)', fontSize: '12px', marginTop: '4px' }}
        >
          {validationError}
        </p>
      )}
    </>
  );
};

export default CustomAutoLogoutControl;
